import React, { useEffect, useRef } from 'react';
import Icon from '@/components/AppIcon';
import AssistantChat from './AssistantChat';
import NewChatButton from './NewChatButton';
import { useAssistant } from '@/contexts/AssistantContext';
import { cn } from '@/utils/cn';

// The header's Agent toggle points at this id (aria-controls).
export const ASSISTANT_POPUP_ID = 'assistant-popup';

// Rendered once at the router level (Routes.jsx) and kept mounted — hidden
// with CSS when closed — so the conversation survives closing the popup and
// navigating between pages.
//
// Keyboard: opening moves focus to the message box, Escape closes, and
// closing hands focus back to whatever opened it (else the Agent toggle).
// Focus used to stay on the page behind on open, and fall to the page body
// on close, because the X that had it was hidden. Non-modal, so no trap.
const AssistantPopup = () => {
  const { isOpen, closeAssistant } = useAssistant();
  const inputRef = useRef(null);
  const openerRef = useRef(null);
  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      const active = document.activeElement;
      openerRef.current = active && active !== document.body ? active : null;
      // After the `flex` class lands: a display:none box takes no focus.
      const frame = window.requestAnimationFrame(() => inputRef.current?.focus());
      wasOpenRef.current = true;
      return () => window.cancelAnimationFrame(frame);
    }
    if (!isOpen && wasOpenRef.current) {
      wasOpenRef.current = false;
      const opener = openerRef.current;
      openerRef.current = null;
      const target =
        opener?.isConnected && !opener.closest?.(`#${ASSISTANT_POPUP_ID}`)
          ? opener
          : document.querySelector(`[aria-controls="${ASSISTANT_POPUP_ID}"]`);
      target?.focus?.();
    }
    return undefined;
  }, [isOpen]);

  return (
    <div
      id={ASSISTANT_POPUP_ID}
      className={cn(
        'fixed bottom-4 right-4 z-50 w-[400px] max-w-[calc(100vw-2rem)] h-[600px] max-h-[calc(100vh-6rem)] flex-col overflow-hidden rounded-xl border border-border bg-card shadow-athletic-lg',
        isOpen ? 'flex' : 'hidden',
      )}
      role="dialog"
      aria-label="Meal assistant chat"
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.stopPropagation();
          closeAssistant();
        }
      }}
    >
      <div className="flex items-center justify-between border-b border-border bg-card px-4 py-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
          <Icon name="Bot" size={18} />
          <span>Meal Assistant</span>
        </div>
        <div className="flex items-center gap-1">
          <NewChatButton />
          <button
            onClick={closeAssistant}
            className="p-1 text-muted-foreground hover:text-foreground transition-athletic"
            aria-label="Close assistant"
          >
            <Icon name="X" size={18} />
          </button>
        </div>
      </div>
      <AssistantChat className="flex-1 min-h-0" inputRef={inputRef} />
    </div>
  );
};

export default AssistantPopup;
